import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import { DonationsAllocation, Investment } from '../api';
import { useHistory } from 'react-router-dom';
import { faPen, faFileChartColumn } from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { DonationVisualProps } from './utils';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { getProgramNameUrl } from '../../ReportBuilder/Components/Budget/utils';

const getDetailClassName = (
  selectedIndex: number | undefined,
  currentIndex: number,
  defaultClassName: string,
): string => {
  if (selectedIndex !== undefined && selectedIndex !== currentIndex) {
    return defaultClassName + ' details-blur';
  }
  return defaultClassName;
};

interface DonationDetailsColumnProps {
  donation: Investment;
  unallocatedAmount: number;
  calculatedTotalDonations: number;
  allocations: DonationsAllocation[];
  donationList: DonationVisualProps[];
  selectedIndex: number | undefined;
  handleDetach: (index: number, allocation: DonationsAllocation) => unknown;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const StyledDiv = styled.div`
  flex: 1;
  .details-blur {
    opacity: 0.5;
  }
  .donation-detail {
    display: flex;
    gap: ${SPACERS.medium};
    margin-bottom: ${SPACERS.large};
  }
  .detail-box {
    width: 16px;
    height: 16px;
    cursor: pointer;
    margin-top: ${SPACERS.extraTiny};
    background-color: ${COLORS.ctaPrimary};
  }
  .donation-detail-extra-wrapper {
    flex: 1;
    .donation-detail-extra {
      cursor: pointer;
      margin-bottom: ${SPACERS.extraTiny};
    }
    .report-status {
      display: flex;
      gap: ${SPACERS.tiny};
      flex-direction: column;
      .report-button {
        display: flex;
        margin-left: -${SPACERS.tiny};
      }
    }
  }
`;

const DonationDetailsColumn: React.FC<DonationDetailsColumnProps> = ({
  allocations,
  donationList,
  selectedIndex,
  handleDetach,
  setSelectedIndex,
}) => {
  const { push } = useHistory();
  if (allocations.length === 0) return null;
  return (
    <StyledDiv>
      {allocations.map((a, i) => {
        const year = a.report?.year;
        const reportId = a.report?.dbId;
        const reportStatus = a.report?.status || 'Unknown';
        const programName = a.report?.program?.name || 'Unknown';
        const onClickIndex = () =>
          setSelectedIndex(selectedIndex === i ? undefined : i);
        return (
          <div
            key={i}
            className={getDetailClassName(selectedIndex, i, 'donation-detail')}
          >
            <div
              className="detail-box"
              style={{ backgroundColor: donationList[i].color }}
              onClick={onClickIndex}
            />
            <div className="donation-detail-extra-wrapper">
              <TrueImpactText
                className="donation-detail-extra"
                onClick={onClickIndex}
              >
                <strong>${numberWithCommas(a.amount)}</strong> of this donation
                went to
                <br />
                <strong>{programName}</strong>, report year{' '}
                <strong>{year}</strong>.
              </TrueImpactText>
              <div className="report-status">
                <TrueImpactText variant="body-small">
                  <strong>Report Status:</strong> {reportStatus}
                </TrueImpactText>
                <div className="report-button">
                  <TrueImpactIconButton
                    icon={faPen}
                    label="Edit Report"
                    text="Edit Report"
                    color="ctaPrimary"
                    onClick={() =>
                      reportId &&
                      push(
                        `/npo/report-builder/${getProgramNameUrl(
                          programName,
                        )}/${reportId}`,
                      )
                    }
                  />
                  <TrueImpactIconButton
                    icon={faFileChartColumn}
                    label="Detach the allocation"
                    text="Detach the allocation"
                    color="critical"
                    onClick={() => handleDetach(i, a)}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </StyledDiv>
  );
};

export default DonationDetailsColumn;
